import { CommonModule, DatePipe } from '@angular/common';
import {
	Component,
	computed,
	inject,
	input,
	signal,
	ViewChild,
} from '@angular/core';
import type { NgForm } from '@angular/forms';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { AlertComponent } from '@valk-nx/components/ui-alert/src/lib/alert.component';
import { RecaptchaNotificationComponent } from '@valk-nx/components/ui-recaptcha-notification/src/lib/recaptcha-notification';
import type { Language } from '@valk-nx/core/lib/core';
import { RECAPTCHA_V2_SITE_KEY } from '@valk-nx/core/lib/core';
import { SharedFormlyModule } from '@valk-nx/formly/lib/formly.module';
import { StoryblokDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok.directive';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { GiftcardService } from '@valk-nx/storyblok-services/src/lib/services/giftcard.service';
import { TagManagerFacade } from '@valk-nx/storyblok-store/src/lib/store/tag-manager/tag-manager.facade';
import type { Link } from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

import { form as formFields } from './balance-giftcard-form';

@Component({
	selector: 'sb-balance-giftcard',
	templateUrl: './balance-giftcard.html',
	imports: [
		AlertComponent,
		CommonModule,
		DatePipe,
		RecaptchaNotificationComponent,
		TranslatePipe,
		SharedFormlyModule,
		StoryblokDirective,
	],
	providers: [TagManagerFacade],
	host: { ngSkipHydration: 'true' },
})
export class BalanceGiftcardComponent extends StoryblokRootDirective {
	private readonly giftcardService = inject(GiftcardService);
	private readonly router = inject(Router);
	private readonly translate = inject(TranslateService);
	private readonly tagManagerFacade = inject(TagManagerFacade);
	readonly recaptchaV2Key = inject(RECAPTCHA_V2_SITE_KEY);

	activateGiftcardPage = input<Link | undefined>(undefined);
	currency = input<string>('EUR');

	giftcardStatus = signal<string>('');
	giftcardSubmitted = signal<boolean>(false);
	giftcardSubmitting = signal<boolean>(false);
	giftcardBalance = signal<number>(0);
	giftcardExpiryDate = signal<string>('');

	@ViewChild('balanceForm') balanceForm!: NgForm;

	showSummaryBalance = computed(() => {
		return (
			this.giftcardStatus() === 'normal' &&
			this.giftcardSubmitted() &&
			this.form.value.cardNumber
		);
	});

	showSummaryInactive = computed(() => {
		return (
			this.giftcardStatus() === 'inactive' &&
			this.giftcardSubmitted() &&
			this.form.value.cardNumber &&
			this.form.value.pinCode
		);
	});

	showSummaryWarning = computed(() => {
		return (
			this.giftcardStatus() !== 'normal' &&
			this.giftcardStatus() !== 'inactive' &&
			this.giftcardSubmitted() &&
			!this.giftcardBalance() &&
			this.form.value.cardNumber &&
			this.form.value.pinCode
		);
	});

	formFields = formFields;
	form = new FormGroup<{
		cardNumber: FormControl;
		pinCode: FormControl;
	}>({
		cardNumber: new FormControl(''),
		pinCode: new FormControl(''),
	});

	language: Language;
	recaptchaVersion: 'v2' | 'v3' = 'v3';

	constructor() {
		super();
		this.language = (this.translate.getCurrentLang() ??
			this.translate.getFallbackLang()) as Language;
	}

	onSubmit(balanceRequest: {
		cardNumber: string;
		pinCode: string;
	}): Promise<{ balance: number; status: string } | null> {
		this.giftcardSubmitting.set(true);
		this.tagManagerFacade.formSubmit('giftcard_saldo_check');
		return this.giftcardService
			.checkBalance(
				balanceRequest.cardNumber,
				balanceRequest.pinCode,
				this.form.get('recaptcha')?.value,
			)
			.then((res) => {
				this.giftcardSubmitted.set(true);
				this.giftcardSubmitting.set(false);
				this.giftcardBalance.set(res.balance);
				this.giftcardStatus.set(res.status);
				this.giftcardExpiryDate.set(res.expiryDate ?? '');
				return {
					balance: res.balance,
					status: res.status,
					expiryDate: res.expiryDate,
				};
			})
			.catch((error: Error) => {
				const status = Number.parseInt(error.message, 10);
				this.giftcardSubmitted.set(
					status < 500 && status !== 0 && status !== 403,
				);
				this.giftcardSubmitting.set(false);
				this.useRecaptchaV2(status);
				return null;
			});
	}

	goToActivateGiftCardForm(): void {
		this.router.navigate([this.activateGiftcardPage()?.cached_url], {
			queryParams: { cardnumber: this.form.controls['cardNumber'].value },
		});
	}

	resetGiftcardForm(): void {
		this.giftcardSubmitted.set(false);
		this.giftcardBalance.set(0);
		this.giftcardStatus.set('');
		this.giftcardExpiryDate.set('');

		this.form.reset();
		this.balanceForm.resetForm();
	}

	changeGiftCardForm(): void {
		this.giftcardSubmitted.set(false);
	}

	useRecaptchaV2(status: number | undefined) {
		if (
			status === 403 &&
			this.recaptchaVersion === 'v3' &&
			this.recaptchaV2Key
		) {
			this.recaptchaVersion = 'v2';
			this.formFields = [
				...this.formFields,
				{
					id: 'recaptcha',
					key: 'recaptcha',
					props: {
						errorMode: 'handled',
						required: true,
						siteKey: this.recaptchaV2Key,
						size: 'default',
					},
					type: 'recaptchaV2',
				},
			];
		}
	}
}
